import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import {
  Plus, PencilSimple, Trash, FloppyDisk, X, ArrowLeft, Eye, ListBullets, BookOpen, ArrowSquareOut
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slugify = (s) =>
  (s || '').toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/[\s-]+/g, '-').slice(0, 80);

const EMPTY_POST = {
  slug: '',
  title: '',
  meta_description: '',
  keywords: '',
  author: 'Dr. Editorial Team, SecondDoctorOpinion.com',
  category: 'Patient Guides',
  cover_image: '',
  excerpt: '',
  body: '',
  read_minutes: 5,
  status: 'published',
  reviewed_by: '',
  last_reviewed: '',
};

export default function AdminBlogManager() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, 'new' or post obj = edit view
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/');
  }, [user, navigate]);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/blog`, { withCredentials: true });
      setPosts(data.posts || []);
    } catch (e) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setForm(EMPTY_POST);
    setEditing('new');
    setShowPreview(false);
  };

  const openEdit = (post) => {
    setForm({
      slug: post.slug || '',
      title: post.title || '',
      meta_description: post.meta_description || '',
      keywords: post.keywords || '',
      author: post.author || EMPTY_POST.author,
      category: post.category || 'Patient Guides',
      cover_image: post.cover_image || '',
      excerpt: post.excerpt || '',
      body: post.body || '',
      read_minutes: post.read_minutes || 5,
      status: post.status || 'published',
      reviewed_by: post.reviewed_by || '',
      last_reviewed: post.last_reviewed || '',
    });
    setEditing(post);
    setShowPreview(false);
  };

  const handleChange = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Auto-fill slug from title only on new posts or empty slug
      if (key === 'title' && (!prev.slug || editing === 'new')) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleSave = async (publish) => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setSaving(true);
    const payload = { ...form, status: publish ? 'published' : 'draft' };
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/blog`, payload, { withCredentials: true });
        toast.success(publish ? 'Article published!' : 'Saved as draft');
      } else {
        await axios.put(`${API}/admin/blog/${editing.id}`, payload, { withCredentials: true });
        toast.success(publish ? 'Article updated and published!' : 'Saved as draft');
      }
      setEditing(null);
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${post.id}`, { withCredentials: true });
      toast.success('Article deleted');
      fetchPosts();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  // ===== Render List View =====
  if (!editing) {
    return (
      <div className="min-h-screen bg-surface-secondary" data-testid="admin-blog-manager">
        <Navbar />
        <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
              <div>
                <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-secondary hover:text-primary mb-2">
                  <ArrowLeft size={14} /> Back to Admin
                </Link>
                <h1 className="font-spectral text-3xl font-bold text-primary flex items-center gap-3">
                  <BookOpen size={28} weight="duotone" /> Blog CMS
                </h1>
                <p className="text-secondary text-sm mt-1">Create, edit and publish patient-facing articles. SEO meta is auto-generated.</p>
              </div>
              <button
                onClick={openNew}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="new-blog-post-button"
              >
                <Plus size={18} weight="bold" /> New Article
              </button>
            </div>

            <div className="card">
              <h2 className="font-spectral text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                <ListBullets size={20} weight="bold" /> All Articles ({posts.length})
              </h2>
              {loading ? (
                <p className="text-secondary text-sm py-6">Loading...</p>
              ) : posts.length === 0 ? (
                <p className="text-secondary text-sm py-6">No articles yet. Create your first one!</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-primary/20 text-secondary">
                        <th className="text-left py-2 px-3 font-medium">Title</th>
                        <th className="text-left py-2 px-3 font-medium">Category</th>
                        <th className="text-left py-2 px-3 font-medium">Status</th>
                        <th className="text-left py-2 px-3 font-medium">Updated</th>
                        <th className="text-right py-2 px-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posts.map((p, idx) => (
                        <tr key={p.id} className="border-b border-primary/10" data-testid={`blog-row-${idx}`}>
                          <td className="py-3 px-3">
                            <div className="font-medium text-primary line-clamp-1">{p.title}</div>
                            <code className="text-xs text-secondary">/blog/{p.slug}</code>
                          </td>
                          <td className="py-3 px-3 text-secondary">{p.category}</td>
                          <td className="py-3 px-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${p.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                              {p.status}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-xs text-secondary">{p.updated_at?.split('T')[0]}</td>
                          <td className="py-3 px-3 text-right space-x-1">
                            <a
                              href={`/blog/${p.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-primary/30 text-primary rounded-full hover:bg-primary/5"
                              data-testid={`view-blog-${idx}`}
                            >
                              <ArrowSquareOut size={12} weight="bold" /> View
                            </a>
                            <button
                              onClick={() => openEdit(p)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-primary text-white rounded-full hover:bg-primary/90"
                              data-testid={`edit-blog-${idx}`}
                            >
                              <PencilSimple size={12} weight="bold" /> Edit
                            </button>
                            <button
                              onClick={() => handleDelete(p)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-300 text-red-600 rounded-full hover:bg-red-50"
                              data-testid={`delete-blog-${idx}`}
                            >
                              <Trash size={12} weight="bold" /> Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ===== Render Edit View =====
  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="admin-blog-editor">
      <Navbar />
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <button
              onClick={() => setEditing(null)}
              className="inline-flex items-center gap-2 text-sm text-secondary hover:text-primary"
              data-testid="back-to-blog-list"
            >
              <ArrowLeft size={14} /> Back to articles
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-primary/30 text-primary text-sm rounded-full hover:bg-primary/5"
                data-testid="toggle-preview"
              >
                <Eye size={14} weight="bold" /> {showPreview ? 'Hide preview' : 'Preview'}
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 border border-primary/30 text-primary text-sm rounded-full hover:bg-primary/5 disabled:opacity-50"
                data-testid="save-draft-button"
              >
                <FloppyDisk size={14} weight="bold" /> Save as Draft
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm rounded-full hover:bg-primary/90 disabled:opacity-50"
                data-testid="publish-button"
              >
                <FloppyDisk size={14} weight="bold" /> {saving ? 'Saving...' : 'Publish'}
              </button>
            </div>
          </div>

          <div className="card">
            <h2 className="font-spectral text-2xl font-bold text-primary mb-1">
              {editing === 'new' ? 'New Article' : 'Edit Article'}
            </h2>
            <p className="text-xs text-secondary mb-6">
              Write in Markdown. Use <code>##</code> for headings, <code>**bold**</code>, <code>*italic*</code>, <code>-</code> for lists, and <code>|</code> for tables.
            </p>

            <div className="space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., How to Prepare for an MRI Scan in India"
                  className="w-full px-4 py-2.5 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary"
                  data-testid="form-title"
                />
              </div>

              {/* Slug + Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">URL Slug</label>
                  <div className="flex items-center bg-surface-secondary rounded-lg border border-primary/20 px-2">
                    <span className="text-xs text-secondary pl-2">/blog/</span>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => handleChange('slug', slugify(e.target.value))}
                      placeholder="auto-generated"
                      className="flex-1 px-2 py-2.5 bg-transparent focus:outline-none text-primary text-sm"
                      data-testid="form-slug"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    className="w-full px-4 py-2.5 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary"
                    data-testid="form-category"
                  >
                    <option>Patient Guides</option>
                    <option>Cardiology</option>
                    <option>Cancer Care</option>
                    <option>Reproductive Medicine</option>
                    <option>Orthopedics</option>
                    <option>Neurology</option>
                    <option>Medical Tourism</option>
                    <option>Mental Health</option>
                    <option>Diabetes & Endocrine</option>
                    <option>Pediatrics</option>
                    <option>News & Updates</option>
                  </select>
                </div>
              </div>

              {/* Cover Image + Read Minutes */}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr,180px] gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Cover Image URL</label>
                  <input
                    type="url"
                    value={form.cover_image}
                    onChange={(e) => handleChange('cover_image', e.target.value)}
                    placeholder="https://images.pexels.com/..."
                    className="w-full px-4 py-2.5 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary text-sm"
                    data-testid="form-cover"
                  />
                  {form.cover_image && (
                    <img
                      src={form.cover_image}
                      alt="cover preview"
                      className="mt-2 w-full max-h-48 object-cover rounded-lg border border-primary/10"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Read Time (min)</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={form.read_minutes}
                    onChange={(e) => handleChange('read_minutes', parseInt(e.target.value) || 5)}
                    className="w-full px-4 py-2.5 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary"
                    data-testid="form-read-minutes"
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Excerpt <span className="text-xs text-secondary">(short summary on /blog grid)</span></label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => handleChange('excerpt', e.target.value)}
                  rows={2}
                  placeholder="One or two sentences that appear in article preview cards."
                  className="w-full px-4 py-2.5 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary text-sm resize-y"
                  data-testid="form-excerpt"
                />
              </div>

              {/* SEO Meta Description + Keywords */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">SEO Meta Description <span className="text-xs text-secondary">(150-160 chars)</span></label>
                  <textarea
                    value={form.meta_description}
                    onChange={(e) => handleChange('meta_description', e.target.value)}
                    rows={3}
                    placeholder="Short summary shown in Google search results."
                    className="w-full px-4 py-2.5 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary text-sm resize-y"
                    data-testid="form-meta-description"
                    maxLength={300}
                  />
                  <p className="text-xs text-secondary mt-1">{form.meta_description.length}/160 chars</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">SEO Keywords <span className="text-xs text-secondary">(comma-separated)</span></label>
                  <textarea
                    value={form.keywords}
                    onChange={(e) => handleChange('keywords', e.target.value)}
                    rows={3}
                    placeholder="e.g., MRI scan India, MRI cost, prepare for MRI"
                    className="w-full px-4 py-2.5 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary text-sm resize-y"
                    data-testid="form-keywords"
                  />
                </div>
              </div>

              {/* Author */}
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Author</label>
                <input
                  type="text"
                  value={form.author}
                  onChange={(e) => handleChange('author', e.target.value)}
                  className="w-full px-4 py-2.5 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary"
                  data-testid="form-author"
                />
              </div>

              {/* Medical review */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Medically reviewed by</label>
                  <input
                    type="text"
                    value={form.reviewed_by}
                    onChange={(e) => handleChange('reviewed_by', e.target.value)}
                    placeholder="e.g., Dr. Ankita Mukherjee, DNB (ENT)"
                    className="w-full px-4 py-2.5 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary"
                    data-testid="form-reviewed-by"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Last reviewed (set only when a doctor re-reviews)</label>
                  <input
                    type="date"
                    value={form.last_reviewed}
                    onChange={(e) => handleChange('last_reviewed', e.target.value)}
                    className="w-full px-4 py-2.5 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary"
                    data-testid="form-last-reviewed"
                  />
                </div>
              </div>

              {/* Body (Markdown) */}
              <div>
                <label className="block text-sm font-medium text-primary mb-1">
                  Body (Markdown) <span className="text-red-500">*</span>
                </label>
                <div className={`grid gap-4 ${showPreview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                  <textarea
                    value={form.body}
                    onChange={(e) => handleChange('body', e.target.value)}
                    rows={20}
                    placeholder={`## Heading 2\n\nParagraph with **bold** and *italic*.\n\n- Bullet 1\n- Bullet 2\n\n### Subheading 3`}
                    className="w-full px-4 py-3 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 text-primary font-mono text-sm resize-y"
                    data-testid="form-body"
                  />
                  {showPreview && (
                    <div
                      className="border border-primary/10 rounded-lg p-5 bg-surface overflow-y-auto max-h-[480px] prose-medical"
                      dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(form.body) }}
                      data-testid="markdown-preview"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// Lightweight markdown preview renderer (mirrors BlogPost.js)
function renderMarkdownPreview(text) {
  const lines = (text || '').split('\n');
  const out = [];
  let inList = false;
  const flushList = () => { if (inList) { out.push('</ul>'); inList = false; } };
  const inline = (s) => s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
  for (const line of lines) {
    const t = line.trim();
    if (t.startsWith('## ')) { flushList(); out.push(`<h2>${inline(t.slice(3))}</h2>`); }
    else if (t.startsWith('### ')) { flushList(); out.push(`<h3>${inline(t.slice(4))}</h3>`); }
    else if (t.startsWith('- ')) { if (!inList) { out.push('<ul>'); inList = true; } out.push(`<li>${inline(t.slice(2))}</li>`); }
    else if (t === '') { flushList(); }
    else { flushList(); out.push(`<p>${inline(t)}</p>`); }
  }
  flushList();
  return out.join('\n');
}
